import { currentMarker } from '../tracks/sectionMarkers';
import { defaultCut } from '../tracks/trackStripModel';
import { playbackUrlFor, trackKey } from '../../../../services/studentTracksModel';

/**
 * Now Playing (2026-10-29): the pure half of the lyrics pane's pinned player.
 * Anthony: "a 'now playing' section that can be detached like the lyrics
 * window … combine the player and the scrolling lyrics … playback controls
 * pinned … so it mimics the apple music app."
 *
 * Back / forward move by SECTION when the track has section markers, the way
 * Apple Music's buttons move by song; a song with none skips 10 seconds.
 * Back follows the music-app rule: more than a moment into a section goes to
 * its start, a second tap goes to the one before.
 */
export const SKIP_SECONDS = 10;
// Past this far into a section, Back restarts it rather than leaving it.
export const RESTART_GRACE = 3;

/**
 * The file the bar plays: this song's open audio track, else the one the
 * lyrics would start (it carries timed words), else the song's default cut.
 * Only files with a Storage copy: the bar is our own transport, and a Drive
 * embed has none. null when the song has nothing to play here.
 */
export const nowPlayingFile = ({ files = [], player = null, song = null, startFile = null } = {}) => {
	const playable = (Array.isArray(files) ? files : []).filter((f) => f && playbackUrlFor(f));
	if (!playable.length) return null;
	const cur = player && player.current;
	if (cur && cur.engine === 'audio') {
		const open = playable.find((f) => trackKey(f) === cur.key);
		if (open) return open;
	}
	if (startFile && playable.some((f) => trackKey(f) === trackKey(startFile))) return startFile;
	return defaultCut(playable, song);
};

/** Where Back goes from `t`: { t, marker, label } (label names the place). */
export const backTarget = (markers = [], t = 0) => {
	if (!markers.length) return { t: Math.max(0, t - SKIP_SECONDS), marker: null, label: `Back ${SKIP_SECONDS} seconds`, short: `${SKIP_SECONDS}s` };
	const cur = currentMarker(markers, t);
	if (cur && t - cur.t > RESTART_GRACE) return { t: cur.t, marker: cur, label: `Back to the start of ${cur.label}`, short: cur.label };
	const i = cur ? markers.indexOf(cur) : -1;
	const prev = i > 0 ? markers[i - 1] : null;
	if (prev) return { t: prev.t, marker: prev, label: `Back to ${prev.label}`, short: prev.label };
	return { t: 0, marker: null, label: 'Back to the start', short: 'Start' };
};

/** Where Forward goes from `t`, or null at the last section. */
export const forwardTarget = (markers = [], t = 0, duration = 0) => {
	if (!markers.length) {
		const to = duration > 0 ? Math.min(duration, t + SKIP_SECONDS) : t + SKIP_SECONDS;
		return { t: to, marker: null, label: `Forward ${SKIP_SECONDS} seconds`, short: `${SKIP_SECONDS}s` };
	}
	const next = markers.find((m) => m.t > t + 0.05);
	return next ? { t: next.t, marker: next, label: `Forward to ${next.label}`, short: next.label } : null;
};

/** A loop over one section: its marker to the next (or the end), or null. */
export const sectionLoop = (markers = [], marker, duration = 0) => {
	if (!marker) return null;
	const i = markers.findIndex((m) => m.sectionId === marker.sectionId);
	if (i < 0) return null;
	const next = markers[i + 1];
	const end = next ? next.t : Number(duration) || 0;
	return end > marker.t ? { start: marker.t, end, sectionId: marker.sectionId, label: marker.label } : null;
};

/** "-2:10", the time left, the way a music app shows the right-hand end. */
export const remaining = (t = 0, duration = 0, format) => {
	if (!(duration > 0)) return format(0);
	return `-${format(Math.max(0, duration - t))}`;
};
